import {
  cacheKeys,
  Issue,
  useDeleteMutation,
  useIssue,
} from '@gripp/shared-logic';
import {
  Button,
  ConfirmationOverlay,
  IssueEdit,
  LoadingIndicator,
  SlideAction,
  Spacing,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useUnsavedChangesBlocker } from '../../shared/hooks/useUnsavedChangesBlocker';

const DELETE_ISSUE = `
mutation deleteIssue($input: MutateIssueInput!) {
  deleteIssue(input: $input) {
    id
  }
}
`;

export const ActivityEditColumn = ({
  selectedLabels,
  onLabelsChange,
  resetRef,
  backGuardRef,
  onOpenLabelsPanel,
}: {
  selectedLabels?: Array<{ id: string; name: string }>;
  onLabelsChange?: (labels: Array<{ id: string; name: string }>) => void;
  resetRef?: RefObject<(() => void) | undefined>;
  backGuardRef?: RefObject<
    ((onProceed: () => void, onCancel?: () => void) => void) | null
  >;
  /**
   * Invoked when the user clicks the labels selector in IssueEdit. Parents
   * should set this to open a sibling third column hosting the labels editor.
   * When omitted, falls back to toggleThirdPanel for backwards compatibility
   * with legacy callers that pass a pre-mounted thirdColumn.
   */
  onOpenLabelsPanel?: () => void;
}) => {
  const { issueId } = useParams();
  const { issue: issueData, isLoading } = useIssue(issueId ?? '');
  const issue = issueData as unknown as Issue | undefined;
  const { prepareSlideTransition, toggleThirdPanel, setColumnOverlay } =
    useSlideLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useDeleteMutation({
    modelName: 'issue',
    query: DELETE_ISSUE,
  });

  const [formIsDirty, setFormIsDirty] = useState(false);
  const [formIsValid, setFormIsValid] = useState(false);
  const onSaveRef = useRef<() => Promise<void>>(() => Promise.resolve());

  const handleFormStateChange = useCallback(
    (isDirty: boolean, isValid: boolean, onSave: () => Promise<void>) => {
      setFormIsDirty(isDirty);
      setFormIsValid(isValid);
      onSaveRef.current = onSave;
    },
    [],
  );

  const { clearDirtyImmediate } = useUnsavedChangesBlocker({
    isDirty: formIsDirty,
    isValid: formIsValid,
    onSave: () => onSaveRef.current(),
  });

  // Register a back-button guard with the parent so the SlideLayout back
  // button shows the unsaved-changes overlay instead of navigating away.
  useEffect(() => {
    if (!backGuardRef) return;
    if (formIsDirty) {
      backGuardRef.current = (onProceed, onCancel) => {
        setColumnOverlay(
          <ConfirmationOverlay
            title={t('unsavedChanges.title')}
            subtitle={t('unsavedChanges.subtitle')}
            confirmLabel={t('unsavedChanges.saveAndExit')}
            onConfirm={async () => {
              if (!formIsValid) return;
              await onSaveRef.current();
              clearDirtyImmediate();
              setColumnOverlay(null);
              onProceed();
            }}
            cancelLabel={t('unsavedChanges.dontSave')}
            onCancel={() => {
              setColumnOverlay(null);
              clearDirtyImmediate();
              onProceed();
            }}
          />,
        );
      };
    } else {
      backGuardRef.current = null;
    }
  }, [
    formIsDirty,
    formIsValid,
    backGuardRef,
    setColumnOverlay,
    t,
    clearDirtyImmediate,
  ]);

  const getRouteHandoffState = (transition: SlideAction) => {
    const currentState =
      location.state && typeof location.state === 'object'
        ? (location.state as Record<string, unknown>)
        : {};

    return {
      ...currentState,
      slideLayoutInitialTransition: transition,
    };
  };

  const getEditSubRoutePath = (subRoute: 'reassign' | 'revise') => {
    const editPath = location.pathname.replace(
      /\/edit(?:\/[^/]+)?\/?$/,
      '/edit',
    );
    return `${editPath}/${subRoute}`;
  };

  const onReassignPress = () => {
    if (location.pathname.includes('/reassign')) {
      return;
    }

    prepareSlideTransition(SlideAction.NEXT);

    navigate(getEditSubRoutePath('reassign'), {
      state: getRouteHandoffState(SlideAction.NEXT),
    });
  };

  const onReviseSubmissionPress = () => {
    if (location.pathname.includes('/revise')) {
      return;
    }

    prepareSlideTransition(SlideAction.NEXT);

    navigate(getEditSubRoutePath('revise'), {
      state: getRouteHandoffState(SlideAction.NEXT),
    });
  };

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (!issue) {
    return null;
  }

  const hideDeleteOverlay = () => setColumnOverlay(null);
  const issueCreatedAt =
    issue.createdAt instanceof Date
      ? issue.createdAt.toISOString()
      : issue.createdAt;

  const handleDeleteConfirm = async () => {
    if (isPending) return;
    await mutateAsync(
      { input: { id: issue.id } },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: cacheKeys('workspaceasset').all,
          });
          hideDeleteOverlay();
          const { from, fromAssetId } =
            (location.state as {
              from?: string;
              fromAssetId?: string;
            } | null) ?? {};

          // Route the user back to the originating context. The issue is
          // deleted, so we can't return to the exact `from` URL — pick the
          // closest valid parent for each asset-scoped variant.
          const assetActivityMatch = from?.match(
            /^\/assets\/([^/]+)\/activity\/[^/]+$/,
          );
          const assetActivitiesMatch = from?.match(
            /^\/assets\/([^/]+)\/activities\/[^/]+$/,
          );

          // Replace the edit URL in history so the deleted-issue entry can't
          // be revisited via back navigation. `afterIssueDelete` tells the
          // target page to avoid navigate(-1) on its back button, since the
          // prior history entry may also reference the deleted issue.
          const navOptions = {
            replace: true,
            state: { afterIssueDelete: true },
          };

          if (assetActivitiesMatch) {
            navigate(`/assets/${assetActivitiesMatch[1]}`, navOptions);
          } else if (assetActivityMatch) {
            navigate(`/assets/${assetActivityMatch[1]}/detail`, navOptions);
          } else if (fromAssetId) {
            navigate(`/assets/${fromAssetId}/detail`, navOptions);
          } else {
            navigate('/activities', navOptions);
          }
        },
      },
    );
  };

  const handleDeletePress = () => {
    setColumnOverlay(
      <ConfirmationOverlay
        title={t('issue.delete.title')}
        subtitle={t('issue.delete.warning')}
        confirmLabel={t('issue.menu.delete')}
        onConfirm={handleDeleteConfirm}
        cancelLabel={t('issue.delete.cancel')}
        onCancel={hideDeleteOverlay}
      />,
    );
  };

  return (
    <IssueEdit
      issue={issue}
      issueId={issue.id}
      currentTitle={issue.title}
      workspaceId={issue.workspace?.id}
      currentLabels={issue.labels}
      currentAdjustCreationDate={issue.adjustCreationDate}
      currentCreatedAt={issueCreatedAt}
      bottomBar={(props: Record<string, unknown>) => {
        const onRightPress = props.onRightPress;
        if (resetRef && typeof onRightPress === 'function') {
          resetRef.current = onRightPress as () => void;
        }
        return null;
      }}
      contentFooter={
        <View style={styles.contentActions}>
          <Button
            style={styles.saveButton}
            onPress={
              formIsDirty && formIsValid ? () => onSaveRef.current() : undefined
            }
            state={formIsDirty && formIsValid ? 'active' : 'default'}
            disabled={!formIsDirty || !formIsValid}
          >
            {t('issue.editModal.save')}
          </Button>
          <Button
            variant="rectangular"
            state="outline"
            style={styles.deleteButton}
            onPress={handleDeletePress}
            size="large"
          >
            {t('issue.delete.title')}
          </Button>
        </View>
      }
      onReassignPress={onReassignPress}
      onReviseSubmissionPress={onReviseSubmissionPress}
      onLabelSelectorEdit={onOpenLabelsPanel ?? toggleThirdPanel}
      externalLabels={selectedLabels}
      onLabelsChange={onLabelsChange}
      onFormStateChange={handleFormStateChange}
    />
  );
};

const styles = StyleSheet.create({
  contentActions: {
    paddingHorizontal: Spacing.baseMargin.base,
    paddingTop: Spacing.baseMargin.base,
    gap: Spacing.baseMargin.base / 4,
  },
  saveButton: {
    width: '100%',
    height: 48,
    borderRadius: 2,
  },
  deleteButton: {
    borderWidth: 0,
    width: '100%',
  },
});
